import { useEffect, useRef, useState } from 'react';

// ── DONNÉES GÉNÉRIQUES ──────────────────────────────────────────────────────
const CAUSALITIES = [
  { cause: 'Ressources disponibles',   effect: 'Capacité d\'action',     w:  0.85 },
  { cause: 'Freins structurels',       effect: 'Inertie',                w: -0.88 },
  { cause: 'Alignement des acteurs',   effect: 'Cohérence',              w:  0.72 },
  { cause: 'Biais cognitifs',          effect: 'Décision sous-optimale', w: -0.79 },
  { cause: 'Pratiques réflexives',     effect: 'Adaptation',             w:  0.68 },
];

const HISTORY = [
  { type: 'DÉCLENCHEUR',   label: 'Prise de conscience collective', time: '14:02:01' },
  { type: 'AJUSTEMENT',    label: 'Reconfiguration des priorités',   time: '14:02:05' },
  { type: 'IMPACT',        label: 'Changement de trajectoire',       time: '14:02:11' },
];

const RADAR = {
  Autonomie: 67, Cohérence: 45, Résilience: 82,
  Agilité: 53, Confiance: 71, Tensions: 38,
};

const ACTIONS = [
  { id: 'a1', label: 'Développer une approche critique et réflexive' },
  { id: 'a2', label: 'Renforcer les procédures et contrôles' },
  { id: 'a3', label: 'Valoriser les ressources et compétences existantes' },
];

const CONCEPTS = ['Systémique', 'Causalité', 'Adaptation', 'Bifurcation'];

const FAMILY_COLORS = {
  École:     '#3b82f6',
  Travail:   '#ef4444',
  Justice:   '#6366f1',
  Art:       '#f59e0b',
  Médiation: '#10b981',
  Famille:   '#8b5cf6',
};

const FAMILIES = Object.entries(FAMILY_COLORS).map(([name, color], i, arr) => ({
  name, color,
  angle: (i / arr.length) * Math.PI * 2 - Math.PI / 2,
  stress: Math.random() * 0.6 + 0.2,
}));

const AGENTS = FAMILIES.flatMap(f =>
  Array.from({ length: Math.floor(Math.random() * 3) + 1 }, (_, j) => ({
    name: `${f.name[0]}${j + 1}`, family: f.name, color: f.color,
  }))
);

const SCENE_COUNT = 4;

// ── CSS-in-JS STYLES (version compacte pour mobile) ───────────────────────────
const S = {
  root: {
    width: '100%', height: '100%', minHeight: '400px',
    background: '#07090f', color: 'rgba(255,255,255,0.9)',
    fontFamily: "'Courier New', monospace", fontSize: '11px',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    borderRadius: '12px', position: 'relative',
  },
  hdr: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 12px', minHeight: '40px', flexShrink: 0,
    background: '#0d1018', borderBottom: '1px solid rgba(255,255,255,0.07)',
  },
  badge: (variant) => ({
    display: 'flex', alignItems: 'center', gap: '5px',
    padding: '3px 8px', borderRadius: '20px',
    fontSize: '9px', letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700,
    ...(variant === 'playing' && { background: 'rgba(16,185,129,.1)', border: '1px solid rgba(16,185,129,.25)', color: '#10b981' }),
    ...(variant === 'action'  && { background: 'rgba(245,158,11,.1)',  border: '1px solid rgba(245,158,11,.25)',  color: '#f59e0b' }),
    ...(variant === 'done'    && { background: 'rgba(167,139,250,.1)', border: '1px solid rgba(167,139,250,.25)', color: '#a78bfa' }),
  }),
  dot: (color) => ({
    width: '6px', height: '6px', borderRadius: '50%',
    background: color === 'g' ? '#10b981' : color === 'a' ? '#f59e0b' : '#a78bfa',
    animation: 'pesaPulse 1.4s ease-in-out infinite',
    flexShrink: 0,
  }),
  hdrTitle: {
    fontSize: '11px', fontWeight: 700, letterSpacing: '.05em',
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.9)',
    flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  progWrap: { display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 },
  progBar: { width: '60px', height: '3px', background: 'rgba(255,255,255,.08)', borderRadius: '2px', overflow: 'hidden' },
  progFill: (pct) => ({
    height: '100%',
    background: 'linear-gradient(90deg, #a78bfa, #f59e0b)',
    borderRadius: '2px', width: `${pct}%`, transition: 'width 1s ease',
  }),
  progPct: { fontSize: '9px', color: 'rgba(255,255,255,0.45)', minWidth: '24px', textAlign: 'right' },
  sceneDots: { display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '4px' },
  sdot: (active) => ({
    border: 'none', cursor: 'pointer', borderRadius: '3px', height: '3px',
    padding: 0, transition: 'all .3s',
    background: active ? '#a78bfa' : 'rgba(167,139,250,.2)',
    width: active ? '16px' : '6px',
  }),
  body: { flex: 1, overflow: 'hidden', position: 'relative' },
};

// ── KEYFRAMES injection ───────────────────────────────────────────────────────
const KEYFRAMES = `
@keyframes pesaPulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:.5; transform:scale(.85); }
}
@keyframes dotpulse {
  0%,100% { opacity:1; }
  50% { opacity:0.3; }
}
`;

function injectKeyframes() {
  if (document.getElementById('pesa-kf')) return;
  const s = document.createElement('style');
  s.id = 'pesa-kf';
  s.textContent = KEYFRAMES;
  document.head.appendChild(s);
}

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────

function WaveCanvas({ canvasRef, isActive = true }) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    let waveOffset = 0;
    let animationId;
    
    const draw = () => {
      const ctx = canvas.getContext('2d');
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      if (!w || !h) {
        animationId = requestAnimationFrame(draw);
        return;
      }
      
      canvas.width = w;
      canvas.height = h;
      ctx.clearRect(0, 0, w, h);
      
      const vol = isActive ? 25 + Math.sin(Date.now() * 0.008) * 15 + Math.random() * 8 : 0;
      ctx.beginPath();
      ctx.strokeStyle = isActive ? '#f59e0b' : 'rgba(245,158,11,0.15)';
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      
      if (vol > 5) {
        const segs = 60;
        ctx.moveTo(0, h / 2);
        for (let i = 0; i <= segs; i++) {
          const base = Math.sin(i * 0.5 + waveOffset) * Math.cos(i * 0.3 - waveOffset * 0.6);
          const env = Math.sin((i / segs) * Math.PI);
          ctx.lineTo(i * (w / segs), h / 2 + base * (vol / 4) * env);
        }
      } else {
        ctx.moveTo(0, h / 2);
        ctx.lineTo(w, h / 2);
      }
      ctx.stroke();
      waveOffset += 0.12;
      animationId = requestAnimationFrame(draw);
    };
    
    draw();
    return () => cancelAnimationFrame(animationId);
  }, [isActive, canvasRef]);
  
  return <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />;
}

function PESACanvas({ canvasRef, wrapRef, isActive = true }) {
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    
    let time = 0;
    let animationId;
    
    const draw = () => {
      const ctx = canvas.getContext('2d');
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (!w || !h) {
        animationId = requestAnimationFrame(draw);
        return;
      }
      
      canvas.width = w;
      canvas.height = h;
      ctx.clearRect(0, 0, w, h);
      
      const cx = w / 2, cy = h / 2;
      const nodes = FAMILIES.map(f => ({
        ...f,
        x: cx + Math.cos(f.angle) * Math.min(w, h) * 0.3,
        y: cy + Math.sin(f.angle) * Math.min(w, h) * 0.3,
      }));
      
      // Liens
      nodes.forEach((a, i) => nodes.slice(i + 1).forEach(b => {
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(cx + Math.sin(time * 0.5) * 6, cy + Math.cos(time * 0.5) * 6, b.x, b.y);
        ctx.strokeStyle = 'rgba(255,255,255,0.03)';
        ctx.lineWidth = 1; ctx.stroke();
      }));
      
      // Liens actifs
      [[0, 2], [1, 4]].forEach(([ai, bi]) => {
        if (!nodes[ai] || !nodes[bi]) return;
        const a = nodes[ai], b = nodes[bi];
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = 'rgba(16,185,129,0.15)'; ctx.lineWidth = 2; ctx.stroke();
        const t2 = (time * 0.015) % 1;
        const px = a.x + (b.x - a.x) * t2, py = a.y + (b.y - a.y) * t2;
        ctx.beginPath(); ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = '#10b981'; ctx.shadowBlur = 8; ctx.shadowColor = '#10b981'; 
        ctx.fill(); ctx.shadowBlur = 0;
      });
      
      // Nœuds
      nodes.forEach(n => {
        const pulse = Math.sin(time * 2.5 + n.x * 0.01) * 1.5;
        const size = 16 + n.stress * 12 + pulse;
        
        if (n.stress > 0.4) {
          const gr = ctx.createRadialGradient(n.x, n.y, size, n.x, n.y, size * 2.2);
          gr.addColorStop(0, n.color + '30'); gr.addColorStop(1, 'transparent');
          ctx.fillStyle = gr;
          ctx.beginPath(); ctx.arc(n.x, n.y, size * 2.2, 0, Math.PI * 2); ctx.fill();
        }
        
        const ng = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, size);
        ng.addColorStop(0, n.color); ng.addColorStop(1, n.color + 'BB');
        ctx.beginPath(); ctx.arc(n.x, n.y, size, 0, Math.PI * 2);
        ctx.fillStyle = ng; ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.4)'; ctx.lineWidth = 1.2; ctx.stroke();
        
        ctx.font = "bold 9px 'Courier New',monospace";
        ctx.fillStyle = '#fff'; ctx.textAlign = 'center';
        ctx.shadowBlur = 3; ctx.shadowColor = 'rgba(0,0,0,.9)';
        ctx.fillText(n.name.toUpperCase(), n.x, n.y - size - 6);
        ctx.shadowBlur = 0;
        
        AGENTS.filter(a => a.family === n.name).forEach((a, idx) => {
          const oa = time + idx * (Math.PI * 2 / 3);
          const ox = n.x + Math.cos(oa) * (size + 8);
          const oy = n.y + Math.sin(oa) * (size + 8);
          ctx.beginPath(); ctx.arc(ox, oy, 2, 0, Math.PI * 2);
          ctx.fillStyle = n.color; ctx.shadowBlur = 4; ctx.shadowColor = n.color;
          ctx.fill(); ctx.shadowBlur = 0;
        });
      });
      
      ctx.font = "8px 'Courier New',monospace";
      ctx.fillStyle = 'rgba(255,255,255,0.2)'; ctx.textAlign = 'left';
      ctx.fillText('Entropy: 0.62', 10, h - 16);
      
      if (isActive) time += 0.008;
      animationId = requestAnimationFrame(draw);
    };
    
    draw();
    return () => cancelAnimationFrame(animationId);
  }, [isActive, canvasRef, wrapRef]);
  
  return <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%', position: 'absolute', inset: 0 }} />;
}

function CausalPanel() {
  return (
    <div style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <span style={{ color: '#a78bfa', fontSize: '10px' }}>⎇</span>
        <span style={{ fontSize: '8px', letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: '#a78bfa' }}>Graphe Causal</span>
      </div>
      <div style={{ padding: '8px 10px' }}>
        {CAUSALITIES.map((c, i) => (
          <div key={i} style={{ marginBottom: '5px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px', fontSize: '9px' }}>
              <span style={{ color: 'rgba(255,255,255,0.45)', flex: '0 0 75px' }}>{c.cause}</span>
              <span style={{ color: '#a78bfa', flexShrink: 0 }}>→</span>
              <span style={{ fontWeight: 700, flex: 1, color: c.w > 0 ? '#10b981' : '#f87171' }}>{c.effect}</span>
            </div>
            <div style={{ height: '2px', background: 'rgba(255,255,255,.05)', borderRadius: '1px', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: '1px', background: c.w > 0 ? '#10b981' : '#f87171', width: `${Math.abs(c.w * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryPanel() {
  return (
    <div style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <span style={{ color: '#f59e0b', fontSize: '10px' }}>◎</span>
        <span style={{ fontSize: '8px', letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: '#f59e0b' }}>Event Log</span>
      </div>
      <div style={{ padding: '8px 10px' }}>
        {HISTORY.map((h, i) => (
          <div key={i} style={{ padding: '5px 8px', borderLeft: '2px solid rgba(245,158,11,.25)', marginBottom: '4px', background: 'rgba(0,0,0,.1)', borderRadius: '0 4px 4px 0' }}>
            <div style={{ fontSize: '8px', color: '#f59e0b', opacity: .6 }}>[{h.time}]</div>
            <div style={{ fontSize: '8px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,.7)' }}>{h.type}</div>
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>{h.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RadarPanel() {
  return (
    <div style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <span style={{ color: '#a78bfa', fontSize: '10px' }}>◈</span>
        <span style={{ fontSize: '8px', letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: '#a78bfa' }}>Analyse Spectrale</span>
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
          {Object.entries(RADAR).map(([k, v]) => (
            <div key={k} style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', padding: '4px 6px' }}>
              <div style={{ fontSize: '7px', textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,0.2)' }}>{k}</div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#a78bfa' }}>{v}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SolutionPanel() {
  return (
    <div style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <span style={{ color: '#f59e0b', fontSize: '10px' }}>◉</span>
        <span style={{ fontSize: '8px', letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: '#f59e0b' }}>Objectif</span>
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', animation: 'pesaPulse 2s ease-in-out infinite', marginRight: '6px' }} />
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>Émancipation critique</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
          <div style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', padding: '5px 7px' }}>
            <div style={{ fontSize: '7px', textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,0.2)' }}>Complexité</div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#10b981' }}>Élevée</div>
          </div>
          <div style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', padding: '5px 7px' }}>
            <div style={{ fontSize: '7px', textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,0.2)' }}>Stabilité</div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#10b981' }}>94%</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConceptsPanel() {
  return (
    <div style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <span style={{ color: '#f59e0b', fontSize: '10px' }}>📖</span>
        <span style={{ fontSize: '8px', letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: '#f59e0b' }}>Cadre Théorique</span>
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {CONCEPTS.map(c => (
            <span key={c} style={{
              display: 'inline-block', padding: '3px 8px', borderRadius: '20px',
              fontSize: '8px', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
              background: 'rgba(245,158,11,.08)', color: '#f59e0b', border: '1px solid rgba(245,158,11,.2)',
            }}>
              # {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PredictionPanel() {
  return (
    <div style={{ background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <span style={{ color: '#f59e0b', fontSize: '10px' }}>●</span>
        <span style={{ fontSize: '8px', letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: '#f59e0b' }}>Prédiction</span>
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
          <span style={{ fontSize: '20px', fontWeight: 700 }}>61.4%</span>
          <span style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '.12em', color: '#10b981' }}>Favorable</span>
        </div>
        <div style={{ height: '4px', background: 'rgba(255,255,255,.05)', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', background: '#10b981', borderRadius: '2px', width: '61.4%', transition: 'width 1.2s ease' }} />
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function PESASimulatorSpot({
  initialStep = 1,
  autoPlay = true,
  showTimeline = true,
}) {
  const [scene, setScene] = useState(initialStep);
  const [selectedAction, setSelectedAction] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileTip, setShowMobileTip] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);

  // Audio ref
  const audioRef = useRef(null);
  
  // Canvas refs
  const introWaveRef = useRef(null);
  const vwaveRef = useRef(null);
  const pesaCanvasRef = useRef(null);
  const pesaWrapRef = useRef(null);

  
  
  // Inject keyframes once
  useEffect(() => { injectKeyframes(); }, []);
  
  // Audio setup
  useEffect(() => {
    if (!autoPlay) return;
    // Créer un contexte audio silencieux pour débloquer l'autoplay sur mobile
    const initAudio = () => {
      if (audioRef.current) {
        audioRef.current.volume = 0.15;
        audioRef.current.loop = false;
        audioRef.current.play().catch(e => console.log('Audio play blocked, waiting for user interaction'));
      }
    };
    
    // Tentative de lecture automatique
    initAudio();
    
    // Sur mobile, on débloque l'audio au premier clic
    const unlockAudio = () => {
      if (audioRef.current && audioRef.current.paused) {
        audioRef.current.play().catch(e => console.log('Still blocked'));
      }
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
    
    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
    
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, [autoPlay]);
  
  // Responsive
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 600);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  
  // Auto-sequence
  useEffect(() => {
    if (!autoPlay || scene !== 0) return;
    const timer1 = setTimeout(() => setScene(1), 3500);
    const timer2 = setTimeout(() => setScene(2), 16000);
    const timer3 = setTimeout(() => setScene(3), 26000);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [autoPlay, scene]);
  
  // Phase config
  const phaseConf = [
    { variant: 'playing', dotColor: 'g', label: 'LIVE',     prog: 0  },
    { variant: 'playing', dotColor: 'g', label: 'PLAYING',  prog: 57 },
    { variant: 'action',  dotColor: 'a', label: 'DÉCISION', prog: 57 },
    { variant: 'done',    dotColor: 'p', label: 'TERMINÉ',  prog: 100 },
  ];
  const ph = phaseConf[scene];
  
  // Scene 1 layout — responsive
  const s1ContentStyle = isMobile
    ? { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1px', background: 'rgba(0,0,0,.5)' }
    : { flex: 1, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '1px', background: 'rgba(0,0,0,.5)' };
  
  const colStyle = { background: '#0d1018', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' };
  const midStyle = isMobile
    ? { background: '#07090f', padding: 0, overflow: 'hidden' }
    : { background: '#07090f', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' };
  
  const pesaHeight = isMobile ? '200px' : '100%';
  
  return (
    <div style={S.root}>
      {/* Audio élément */}
      {autoPlay && (
        <audio ref={audioRef} loop preload="auto">
          <source src="/audio/intro-pesa.mp3" preload="auto" />
        </audio>
      )}
      
      {/* HEADER */}
      <div style={S.hdr}>
        <div style={S.badge(ph.variant)}>
          <span style={S.dot(ph.dotColor)} />
          <span>{ph.label}</span>
        </div>
        <span style={S.hdrTitle}>Violence Symbolique</span>
        <div style={S.progWrap}>
          <div style={S.progBar}><div style={S.progFill(ph.prog)} /></div>
          <span style={S.progPct}>{ph.prog}%</span>
        </div>
        {showTimeline && (
          <div style={S.sceneDots}>
            {Array.from({ length: SCENE_COUNT }, (_, i) => (
              <button key={i} style={S.sdot(i === scene)} onClick={() => setScene(i)} title={`Scène ${i}`} />
            ))}
          </div>
        )}
      </div>
      
      {/* BODY */}
      <div style={S.body}>
        
        {/* SCENE 0: INTRO */}
        {scene === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', gap: '16px', flexDirection: 'column' }}>
            <div style={{ width: '100%', maxWidth: '400px', background: '#0d1018', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ fontSize: '8px', letterSpacing: '.25em', textTransform: 'uppercase', color: '#a78bfa', opacity: .7, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ display: 'inline-block', width: '5px', height: '5px', borderRadius: '50%', background: '#a78bfa', animation: 'pesaPulse 1.5s ease-in-out infinite' }} />
                  Signal PESACORE · Encodage actif
                </div>
                <div style={{ fontSize: '22px', fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '-.02em', lineHeight: 1.1, marginBottom: '4px' }}>Violence<br />Symbolique</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)' }}>7 étapes · 240 min · Bourdieu · Freire</div>
              </div>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,.2)' }}>
                <div style={{ fontSize: '8px', letterSpacing: '.2em', textTransform: 'uppercase', color: '#a78bfa', opacity: .5, marginBottom: '6px' }}>Analyse spectrale</div>
                <div style={{ height: '40px' }}><WaveCanvas canvasRef={introWaveRef} isActive={scene === 0} /></div>
              </div>
              <div style={{ padding: '10px 14px', margin: '12px 20px', borderLeft: '2px solid #a78bfa', borderRadius: '0 4px 4px 0', fontStyle: 'italic', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, fontSize: '11px' }}>
                "Comment le système scolaire transforme les inégalités sociales en mérites individuels ?"
              </div>
              <div style={{ padding: '16px 20px' }}>
                <button onClick={() => setScene(1)} style={{ width: '100%', padding: '12px', background: 'rgba(167,139,250,.08)', border: '1px solid rgba(167,139,250,.35)', borderRadius: '8px', cursor: 'pointer', color: '#a78bfa', fontFamily: "'Courier New', monospace", fontSize: '11px', fontWeight: 700, letterSpacing: '.15em', textTransform: 'uppercase', transition: 'all .3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>⚡</span> Initialiser le champ
                </button>
                <div style={{ marginTop: '8px', textAlign: 'center', fontSize: '8px', letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(167,139,250,.3)' }}>Simulation systémique irréversible</div>
              </div>
            </div>
          </div>
        )}
        
        {/* SCENE 1: INTERFACE */}
        {scene === 1 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Header de step */}
          <div style={{ 
            padding: '10px 12px', 
            background: '#0d1018', 
            borderBottom: '1px solid rgba(255,255,255,0.07)', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px', 
            flexShrink: 0 
          }}>
            <span style={{ fontSize: '9px', letterSpacing: '.15em', textTransform: 'uppercase', color: '#f59e0b', opacity: .7 }}>STEP 03/07</span>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.9)', flex: 1 }}>Mécanismes de reproduction </span>
            
            {isMobile && (
              <button 
                onClick={() => setShowFullAnalysis(true)}
                style={{
                  fontSize: '9px',
                  padding: '4px 10px',
                  background: 'rgba(167,139,250,0.1)',
                  border: '1px solid rgba(167,139,250,0.4)',
                  borderRadius: '6px',
                  color: '#a78bfa',
                  whiteSpace: 'nowrap'
                }}
              >
                Analyse complète
              </button>
            )}
          </div>

          {/* Contenu principal */}
          <div style={s1ContentStyle}>
            {isMobile ? (
              /* ==================== VERSION MOBILE ==================== */
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                background: '#07090f',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Canvas central agrandi */}
                <div ref={pesaWrapRef} style={{ 
                  position: 'relative', 
                  flex: 1, 
                  minHeight: 0 
                }}>
                  <PESACanvas canvasRef={pesaCanvasRef} wrapRef={pesaWrapRef} isActive={true} />
                  
                  {/* Overlay info discret */}
                  <div style={{ 
                    position: 'absolute', 
                    top: '12px', 
                    left: '12px', 
                    right: '12px', 
                    display: 'flex', 
                    gap: '6px',
                    pointerEvents: 'none' 
                  }}>
                    <div style={{ 
                      background: 'rgba(0,0,0,.75)', 
                      border: '1px solid rgba(255,255,255,0.12)', 
                      borderRadius: '8px', 
                      padding: '4px 9px',
                      fontSize: '8px',
                      color: '#a78bfa'
                    }}>
                      STEP 03/07 • NARRATIVE
                    </div>
                  </div>

                  {/* Zone voix narrative en bas */}
                  <div style={{ 
                    position: 'absolute', 
                    bottom: '12px', 
                    left: '12px', 
                    right: '12px',
                    background: 'rgba(0,0,0,.85)', 
                    border: '1px solid rgba(167,139,250,.25)', 
                    borderRadius: '10px', 
                    padding: '10px 12px'
                  }}>
                    <div style={{ fontSize: '7px', letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: '4px' }}>
                      NEURAL VOICE OUTPUT
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)', fontWeight: 700, lineHeight: 1.4, marginBottom: '6px' }}>
                      Mécanismes de reproduction 
                    </div>
                    <div style={{ height: '26px' }}>
                      <WaveCanvas canvasRef={vwaveRef} isActive={true} />
                    </div>
                  </div>
                </div>

                {/* Tip mobile */}
                <div style={{
                  padding: '10px 12px',
                  background: 'rgba(245,158,11,0.08)',
                  borderTop: '1px solid rgba(245,158,11,0.2)',
                  fontSize: '9px',
                  color: '#f59e0b',
                  textAlign: 'center',
                  lineHeight: 1.4
                }}>
                  Expérience optimale sur <strong>tablette</strong> ou <strong>desktop</strong>.<br />
                  <span style={{ opacity: 0.8 }}>Les processus complets sont masqués pour lisibilité.</span>
                </div>
              </div>
            ) : (
              /* ==================== VERSION DESKTOP/TABLETTE (inchangée) ==================== */
              <>
              {/* LEFT */}
              <div style={colStyle}>
                <PredictionPanel />
                <CausalPanel />
                <HistoryPanel />
              </div>
              {/* MID: PESA canvas */}
              <div style={midStyle}>
                <div ref={pesaWrapRef} style={{ position: 'relative', flex: 1, minHeight: 0, height: pesaHeight, flexShrink: 0, overflow: 'hidden' }}>
                  <PESACanvas canvasRef={pesaCanvasRef} wrapRef={pesaWrapRef} isActive={scene === 1} />
                  <div style={{ position: 'absolute', top: '8px', left: '8px', right: '8px', display: 'flex', gap: '6px', pointerEvents: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '12px', background: 'rgba(0,0,0,.7)', border: '1px solid rgba(255,255,255,0.12)', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '.1em', color: '#a78bfa' }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#a78bfa', animation: 'pesaPulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
                      narrative
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '12px', background: 'rgba(0,0,0,.7)', border: '1px solid rgba(255,255,255,0.12)', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '.1em', color: '#f59e0b' }}>
                      STEP 03/07
                    </div>
                  </div>
                  <div style={{ position: 'absolute', bottom: '8px', left: '8px', right: '8px', pointerEvents: 'none' }}>
                    <div style={{ background: 'rgba(0,0,0,.75)', border: '1px solid rgba(167,139,250,.2)', borderRadius: '10px', padding: '8px 10px' }}>
                      <div style={{ fontSize: '7px', letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: '4px' }}>Neural Voice Output</div>
                      <div style={{ fontSize: isMobile ? '10px' : '12px', color: 'rgba(255,255,255,0.9)', fontWeight: 700, marginBottom: '6px', lineHeight: 1.3 }}>Analyse systémique </div>
                      <div style={{ height: isMobile ? '24px' : '32px' }}><WaveCanvas canvasRef={vwaveRef} isActive={scene === 1} /></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* RIGHT */}
              <div style={colStyle}>
                <RadarPanel />
                <SolutionPanel />
                <ConceptsPanel />
              </div>
              </>
              )}
            </div>
          </div>
        )}
        
        {/* SCENE 2: ACTION */}
        {scene === 2 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 12px', background: '#0d1018', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <span style={{ fontSize: '9px', letterSpacing: '.15em', textTransform: 'uppercase', color: '#f59e0b', opacity: .7, flexShrink: 0 }}>DÉCISION</span>
              <span style={{ fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.9)', flex: 1, minWidth: 0 }}>Quelle intervention ?</span>
            </div>
            <div style={{ margin: '10px 12px', padding: '12px 14px', background: 'rgba(0,0,0,.3)', border: '1px solid rgba(245,158,11,.15)', borderRadius: '10px' }}>
              <div style={{ fontSize: '13px', fontStyle: 'italic', color: 'rgba(255,255,255,.9)', lineHeight: 1.5, marginBottom: '8px' }}>
                "Le système reproduit les inégalités. Quelle intervention peut briser le cycle ?"
              </div>
              <div style={{ fontSize: '9px', padding: '6px 8px', borderRadius: '4px', border: '1px dashed rgba(167,139,250,.25)', background: 'rgba(167,139,250,.05)', color: '#a78bfa', lineHeight: 1.4 }}>
                Challenge : peut-on émanciper sans reproduire un nouvel ordre symbolique ?
              </div>
            </div>
            <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, overflowY: 'auto' }}>
              {ACTIONS.map((a, i) => (
                <button key={a.id} onClick={() => setSelectedAction(a.id)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                  color: selectedAction === a.id ? '#f59e0b' : 'rgba(255,255,255,0.9)',
                  fontFamily: "'Courier New', monospace", fontSize: '11px', width: '100%',
                  transition: 'all .2s',
                  background: selectedAction === a.id ? 'rgba(245,158,11,.08)' : 'rgba(0,0,0,.3)',
                  border: `1px solid ${selectedAction === a.id ? 'rgba(245,158,11,.5)' : 'rgba(255,255,255,0.12)'}`,
                }}>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)', marginRight: '10px', flexShrink: 0 }}>0{i + 1}</span>
                  <span style={{ flex: 1 }}>{a.label}</span>
                  <span style={{ fontSize: '14px', opacity: selectedAction === a.id ? 1 : 0, transition: 'opacity .2s', flexShrink: 0, color: '#f59e0b' }}>⚡</span>
                </button>
              ))}
            </div>
            <button onClick={() => setScene(3)} style={{
              margin: '10px 12px', padding: '12px', width: 'calc(100% - 24px)',
              background: 'rgba(99,102,241,.15)', border: '1px solid rgba(99,102,241,.4)',
              borderRadius: '8px', cursor: 'pointer', color: '#818cf8',
              fontFamily: "'Courier New', monospace", fontSize: '11px',
              fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all .3s',
            }}>
              EXECUTE NEXT STAGE →
            </button>
          </div>
        )}
        
        {/* SCENE 3: REPORT */}
        {scene === 3 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', textAlign: 'center' }}>
            <div style={{ maxWidth: '360px', width: '100%' }}>
              <div style={{ fontSize: '8px', letterSpacing: '.5em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: '12px' }}>Analyse terminée</div>
              <div style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '.08em', lineHeight: 1.1, marginBottom: '4px' }}>
                Étudier <span style={{ fontWeight: 900, fontStyle: 'italic', color: '#a78bfa' }}>l'Inertie.</span>
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginBottom: '20px', lineHeight: 1.6 }}>
                Simulation complétée. Trajectoire validée à{' '}
                <span style={{ color: '#a78bfa', fontWeight: 700 }}>61.4%</span>{' '}
                selon données 1900–2025.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' }}>
                <div style={{ background: 'rgba(0,0,0,.3)', borderRadius: '8px', padding: '10px 8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 900, marginBottom: '2px', color: '#a78bfa' }}>61.4%</div>
                  <div style={{ fontSize: '7px', textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(255,255,255,0.2)' }}>Probabilité</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,.3)', borderRadius: '8px', padding: '10px 8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 900, marginBottom: '2px', color: '#f59e0b' }}>7</div>
                  <div style={{ fontSize: '7px', textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(255,255,255,0.2)' }}>Étapes</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,.3)', borderRadius: '8px', padding: '10px 8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 900, marginBottom: '2px', color: '#10b981' }}>+14%</div>
                  <div style={{ fontSize: '7px', textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(255,255,255,0.2)' }}>Bifurcation</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                {[['−30%', 'Turnover réduit'], ['+78%', 'Engagement moyen'], ['×9', 'ROI formation']].map(([v, l]) => (
                  <div key={l} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '6px', background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,0.07)', fontSize: '11px' }}>
                    <span style={{ color: '#10b981' }}>✓</span>
                    <span style={{ color: '#10b981', fontWeight: 700, minWidth: '36px' }}>{v}</span>
                    <span style={{ color: 'rgba(255,255,255,0.45)' }}>{l}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setScene(0)} style={{
                width: '100%', padding: '12px', background: 'rgba(255,255,255,.03)',
                border: '1px solid rgba(255,255,255,.15)', borderRadius: '8px', cursor: 'pointer',
                color: 'rgba(255,255,255,.7)', fontFamily: "'Courier New', monospace",
                fontSize: '11px', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all .3s',
              }}>
                ↺ Recommencer
              </button>
              <div style={{ marginTop: '10px', fontSize: '8px', letterSpacing: '.15em', color: 'rgba(255,255,255,.1)' }}>
                PESACORE_SIM // VIOLENCE-SYMBOLIQUE // 2026
              </div>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
}